import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Building2, CreditCard, Key, Users, Save, Upload, Zap, Ban, Settings as SettingsIcon } from 'lucide-react';
import { api } from '../services/api';
import TeamManagement from '../components/TeamManagement';
import IntegrationsTab from '../components/IntegrationsTab';
import BillingTab from '../components/BillingTab';
import BlockedIpsTab from '../components/BlockedIpsTab';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  profile_picture_url?: string;
  role: string;
}

interface DaySchedule {
  enabled: boolean;
  open: string;
  close: string;
}

interface OfficeHours {
  timezone: string;
  schedule: {
    monday?: DaySchedule;
    tuesday?: DaySchedule;
    wednesday?: DaySchedule;
    thursday?: DaySchedule;
    friday?: DaySchedule;
    saturday?: DaySchedule;
    sunday?: DaySchedule;
  };
}

interface OrganizationSettings {
  name: string;
  subdomain: string;
  officeHours?: OfficeHours;
}

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export default function Settings() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'profile' | 'organization' | 'team' | 'billing' | 'api' | 'integrations' | 'blockedIps'>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Organization state
  const [orgSettings, setOrgSettings] = useState<OrganizationSettings>({
    name: '',
    subdomain: '',
    officeHours: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      schedule: {
        monday: { enabled: true, open: '09:00', close: '17:00' },
        tuesday: { enabled: true, open: '09:00', close: '17:00' },
        wednesday: { enabled: true, open: '09:00', close: '17:00' },
        thursday: { enabled: true, open: '09:00', close: '17:00' },
        friday: { enabled: true, open: '09:00', close: '17:00' },
        saturday: { enabled: false, open: '09:00', close: '17:00' },
        sunday: { enabled: false, open: '09:00', close: '17:00' },
      },
    },
  });

  const getAuthHeaders = (): HeadersInit => {
    const tokens = localStorage.getItem('auth_tokens');
    if (!tokens) {
      return { 'Content-Type': 'application/json' };
    }
    const { accessToken } = JSON.parse(tokens);
    return {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  useEffect(() => {
    loadSettings();

    // Check for settingsTab parameter in URL (used when navigating from other pages)
    const params = new URLSearchParams(window.location.search);
    const settingsTabParam = params.get('settingsTab');
    if (settingsTabParam && ['profile', 'organization', 'team', 'billing', 'api', 'integrations'].includes(settingsTabParam)) {
      setActiveTab(settingsTabParam as any);
    }
  }, []);

  const loadSettings = async () => {
    try {
      // Load user profile
      const userData = await api.getProfile();
      setProfile(userData);
      setProfileForm({
        name: userData.name || '',
        email: userData.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Load organization settings
      const orgData = await api.getOrganizationSettings();
      setOrgSettings(orgData);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmPassword) {
      showError(t('settings.passwordMismatch'));
      return;
    }

    setSaving(true);
    try {
      await api.updateProfile({
        name: profileForm.name,
        email: profileForm.email,
        ...(profileForm.newPassword && {
          currentPassword: profileForm.currentPassword,
          newPassword: profileForm.newPassword,
        }),
      });

      showSuccess(t('settings.profileUpdated'));
      setProfileForm({ ...profileForm, currentPassword: '', newPassword: '', confirmPassword: '' });
      loadSettings();
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      showError(error.response?.data?.error || t('settings.profileUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError(t('settings.invalidImageType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError(t('settings.imageTooLarge'));
      return;
    }

    setUploadingImage(true);
    try {
      const data = await api.uploadProfilePicture(file);
      // Add cache-busting timestamp to force image reload
      const imageUrl = `${data.url}?t=${Date.now()}`;
      setProfile(prev => prev ? { ...prev, profile_picture_url: imageUrl } : null);
      showSuccess(t('settings.profilePictureUpdated'));
    } catch (error: any) {
      console.error('Failed to upload image:', error);
      showError(error.response?.data?.error || t('settings.imageUploadFailed'));
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleOrgUpdate = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/organization`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(orgSettings),
      });

      if (response.ok) {
        showSuccess(t('settings.orgSettingsUpdated'));
        loadSettings();
      } else {
        const error = await response.json();
        showError(error.error || t('settings.updateFailed'));
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      showError(t('settings.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      {/* Profile Picture */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">{t('settings.profilePicture')}</h3>
        <div className="flex items-center gap-6">
          <div className="relative">
            {profile?.profile_picture_url ? (
              <img
                src={profile.profile_picture_url}
                alt={profile.name}
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-12 h-12 text-gray-400" />
              </div>
            )}
            {uploadingImage && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="text-white text-xs">{t('common.uploading')}</div>
              </div>
            )}
          </div>
          <div>
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition">
              <Upload className="w-4 h-4" />
              <span>{profile?.profile_picture_url ? t('settings.updateProfilePicture') : t('settings.uploadPhoto')}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploadingImage}
              />
            </label>
            <p className="text-xs text-gray-500 mt-2">{t('settings.imageRequirements')}</p>
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">{t('settings.profileInformation')}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.name')}</label>
            <input
              type="text"
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
            <input
              type="email"
              value={profileForm.email}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.role')}</label>
            <input
              type="text"
              value={profile?.role || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">{t('settings.changePassword')}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.currentPassword')}</label>
            <input
              type="password"
              value={profileForm.currentPassword}
              onChange={(e) => setProfileForm({ ...profileForm, currentPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.newPassword')}</label>
            <input
              type="password"
              value={profileForm.newPassword}
              onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.confirmNewPassword')}</label>
            <input
              type="password"
              value={profileForm.confirmPassword}
              onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleProfileUpdate}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? t('common.saving') : t('settings.saveChanges')}</span>
        </button>
      </div>
    </div>
  );

  const renderOrganizationTab = () => (
    <div className="space-y-6">
      {/* Business Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">{t('settings.businessInformation')}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.organizationName')}</label>
            <input
              type="text"
              value={orgSettings.name}
              onChange={(e) => setOrgSettings({ ...orgSettings, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.subdomain')}</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={orgSettings.subdomain}
                disabled
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <span className="text-sm text-gray-500">.tahc.ai</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('settings.subdomainImmutable')}</p>
          </div>
        </div>
      </div>

      {/* Office Hours */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">{t('settings.officeHours')}</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.timezone')} <span className="text-xs text-gray-500">({t('settings.autoDetected')}: {Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
            </label>
            <select
              value={orgSettings.officeHours?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              onChange={(e) => setOrgSettings({
                ...orgSettings,
                officeHours: { ...orgSettings.officeHours!, timezone: e.target.value }
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            >
              <optgroup label="US Timezones">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Phoenix">Arizona (MST)</option>
                <option value="America/Anchorage">Alaska (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii (HST)</option>
              </optgroup>
              <optgroup label="Canada">
                <option value="America/Toronto">Toronto (ET)</option>
                <option value="America/Vancouver">Vancouver (PT)</option>
                <option value="America/Edmonton">Edmonton (MT)</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Europe/Berlin">Berlin (CET)</option>
                <option value="Europe/Amsterdam">Amsterdam (CET)</option>
                <option value="Europe/Madrid">Madrid (CET)</option>
                <option value="Europe/Rome">Rome (CET)</option>
              </optgroup>
              <optgroup label="Asia">
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Asia/Singapore">Singapore (SGT)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Kolkata">India (IST)</option>
              </optgroup>
              <optgroup label="Australia">
                <option value="Australia/Sydney">Sydney (AEDT)</option>
                <option value="Australia/Melbourne">Melbourne (AEDT)</option>
                <option value="Australia/Perth">Perth (AWST)</option>
              </optgroup>
            </select>
          </div>

          {/* Day schedule */}
          <div className="space-y-2">
            <p className="text-xs text-gray-500 mb-3">{t('settings.businessHoursDescription')}</p>
            {(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).map((day) => {
              const daySchedule = orgSettings.officeHours?.schedule[day] || { enabled: false, open: '09:00', close: '17:00' };
              return (
                <div key={day} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3 w-40">
                    <input
                      type="checkbox"
                      id={`day-${day}`}
                      checked={daySchedule.enabled}
                      onChange={(e) => setOrgSettings({
                        ...orgSettings,
                        officeHours: {
                          ...orgSettings.officeHours!,
                          schedule: {
                            ...orgSettings.officeHours!.schedule,
                            [day]: { ...daySchedule, enabled: e.target.checked }
                          }
                        }
                      })}
                      className="w-4 h-4 text-black border-gray-300 rounded"
                    />
                    <label htmlFor={`day-${day}`} className="text-sm font-medium text-gray-700 capitalize cursor-pointer">
                      {day}
                    </label>
                  </div>
                  {daySchedule.enabled && (
                    <>
                      <input
                        type="time"
                        value={daySchedule.open}
                        onChange={(e) => setOrgSettings({
                          ...orgSettings,
                          officeHours: {
                            ...orgSettings.officeHours!,
                            schedule: {
                              ...orgSettings.officeHours!.schedule,
                              [day]: { ...daySchedule, open: e.target.value }
                            }
                          }
                        })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                      />
                      <span className="text-gray-500">{t('settings.to')}</span>
                      <input
                        type="time"
                        value={daySchedule.close}
                        onChange={(e) => setOrgSettings({
                          ...orgSettings,
                          officeHours: {
                            ...orgSettings.officeHours!,
                            schedule: {
                              ...orgSettings.officeHours!.schedule,
                              [day]: { ...daySchedule, close: e.target.value }
                            }
                          }
                        })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </>
                  )}
                  {!daySchedule.enabled && (
                    <span className="text-sm text-gray-400">{t('settings.closed')}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleOrgUpdate}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? t('common.saving') : t('settings.saveChanges')}</span>
        </button>
      </div>
    </div>
  );

  const renderTeamTab = () => (
    <TeamManagement currentUser={profile} />
  );

  const renderBillingTab = () => <BillingTab />;

  const renderAPITab = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
      <p className="text-gray-600 mb-2">{t('settings.apiKeys')}</p>
      <p className="text-sm text-gray-500">{t('settings.apiComingSoon')}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">{t('settings.loadingSettings')}</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 min-h-0">
      {/* Success/Error Notifications */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg flex items-start gap-3 max-w-md animate-fade-in">
          <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      {errorMessage && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg flex items-start gap-3 max-w-md animate-fade-in">
          <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">{errorMessage}</p>
          </div>
          <button onClick={() => setErrorMessage('')} className="text-red-600 hover:text-red-800">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <SettingsIcon className="w-6 h-6 text-gray-900 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('nav.settings')}</h1>
              <p className="text-xs md:text-sm text-gray-500 mt-1">
                {t('settings.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 overflow-x-auto flex-shrink-0">
        <div className="flex gap-4 md:gap-6 min-w-max">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'profile'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{t('settings.profile')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('organization')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'organization'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span>{t('settings.organization')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'team'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>{t('nav.team')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'billing'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span>{t('nav.billing')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('api')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'api'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              <span>{t('settings.api')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'integrations'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>{t('nav.integrations')}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('blockedIps')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition whitespace-nowrap ${
              activeTab === 'blockedIps'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4" />
              <span>Blocked IPs</span>
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 md:p-6 min-h-0">
        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'organization' && renderOrganizationTab()}
        {activeTab === 'team' && renderTeamTab()}
        {activeTab === 'billing' && renderBillingTab()}
        {activeTab === 'api' && renderAPITab()}
        {activeTab === 'integrations' && <IntegrationsTab />}
        {activeTab === 'blockedIps' && <BlockedIpsTab />}
      </div>
    </div>
  );
}
